import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { HttpError, type VoucherView } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { customerDossierRepository } from '@web/data/repositories';
import { currentLocale, t } from '@web/i18n';
import { type AppIconName } from '@web/ui/core/atoms';
import { CustomerAvatar, CustomerProfilePanel, EmptyHint, TabBar } from '@web/ui/core';
import { Scaffold } from '@web/ui/core/organisms';
import { useOrgContext } from '@web/ui/core/hooks';

import { CustomerCompanyTab } from './customer_company_tab';
import { CustomerInactiveTab } from './customer_inactive_tab';
import { CustomerVoucherDrawer } from '../components/customer_voucher_drawer';

type CustomerTab = 'profile' | 'inactive' | 'company';
type CustomerDossier = Awaited<ReturnType<typeof customerDossierRepository.getDossier>>;

const TABS: readonly CustomerTab[] = ['profile', 'inactive', 'company'];

const TAB_LABEL: Record<CustomerTab, Parameters<typeof t>[0]> = {
  profile: 'customers.tab.profile',
  inactive: 'customers.tab.inactive',
  company: 'customers.tab.company',
};

const TAB_ICON: Record<CustomerTab, AppIconName> = {
  profile: 'identity',
  inactive: 'prohibit',
  company: 'building-office',
};

function isCustomerTab(value: unknown): value is CustomerTab {
  return typeof value === 'string' && (TABS as readonly string[]).includes(value);
}

/** "<empresa> · Cliente desde <mês ano> · <email>" — the header subtitle, with the
 * "Cliente desde" clause dropped when the join date is absent or the epoch. */
function monthYear(value: Date | null | undefined): string | null {
  if (!value || value.getTime() === 0) return null;
  return value.toLocaleDateString(currentLocale(), {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Full-page read-only customer screen, rebuilt to the "Viajante" handoff. The
 * `Scaffold` body is capped to a centered reading column; the identity header,
 * sticky brand tabs and the active tab panel stack inside it. A single dossier
 * query feeds the three tabs (Perfil, Cancelados, Empresa). Any voucher row in
 * Cancelados opens the one shared detail drawer, whose selected voucher is
 * page-level UI state.
 */
export function CustomerDetailPage(): React.JSX.Element {
  const { orgId, orgSlug } = useOrgContext();
  const { id, tab } = useParams({ strict: false });
  const customerId = id ?? '';
  const [drawerVoucher, setDrawerVoucher] = useState<VoucherView | null>(null);

  const dossierQuery = useQuery({
    queryKey: queryKeys.customerDossier.detail(orgId, customerId),
    queryFn: () => customerDossierRepository.getDossier(orgId, customerId),
  });

  if (dossierQuery.isLoading) {
    return (
      <Scaffold
        body={<p className="text-meta text-muted-foreground">{t('customers.loading')}</p>}
      />
    );
  }

  const notFound = dossierQuery.error instanceof HttpError && dossierQuery.error.status === 404;
  if (notFound) {
    return (
      <Scaffold
        body={<EmptyHint testId="customers-not-found">{t('customers.notFound')}</EmptyHint>}
      />
    );
  }

  if (dossierQuery.isError || !dossierQuery.data) {
    return (
      <Scaffold
        body={<p className="text-meta text-tone-red-fg">{t('customers.errorLoading')}</p>}
      />
    );
  }

  const dossier = dossierQuery.data;
  const activeTab: CustomerTab = isCustomerTab(tab) ? tab : 'profile';
  const since = monthYear(dossier.profile.createdAt);

  const TAB_COUNT: Record<CustomerTab, number | undefined> = {
    profile: undefined,
    inactive: dossier.trips.inactiveVouchers.length,
    company: undefined,
  };

  const activeTabContent = customerTabContent(activeTab, dossier, setDrawerVoucher);

  return (
    <Scaffold
      body={
        <div className="flex flex-col">
          <header className="mx-auto flex w-full max-w-reading-w items-center gap-4 pb-5">
            <CustomerAvatar name={dossier.profile.name} vip={false} size="lg" />
            <div className="flex min-w-0 flex-col gap-0.5">
              <h1 className="truncate font-serif text-identity-name font-normal leading-card-title tracking-display-condensed text-foreground">
                {dossier.profile.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2 text-base text-muted-foreground">
                {dossier.company?.name ? <span>{dossier.company.name}</span> : null}
                {dossier.company?.name && since ? (
                  <span className="text-faint-foreground">·</span>
                ) : null}
                {since ? <span>{t('customers.header.clientSince', { when: since })}</span> : null}
                {dossier.profile.email ? (
                  <>
                    <span className="text-faint-foreground">·</span>
                    <span className="font-mono text-micro">{dossier.profile.email}</span>
                  </>
                ) : null}
              </div>
            </div>
          </header>

          <nav className="sticky top-0 z-10 -mx-8 bg-paper/90 px-8 backdrop-blur-sm">
            <div className="mx-auto w-full max-w-reading-w">
              <TabBar ariaLabel={t('customers.tabsAria')}>
                {TABS.map((id_) => (
                  <TabBar.Tab
                    key={id_}
                    accent="brand"
                    to="/orgs/$orgSlug/customers/$id/$tab"
                    params={{ orgSlug, id, tab: id_ }}
                    icon={TAB_ICON[id_]}
                    label={t(TAB_LABEL[id_])}
                    count={TAB_COUNT[id_]}
                    isActive={id_ === activeTab}
                  />
                ))}
              </TabBar>
            </div>
          </nav>

          <div className="mx-auto w-full max-w-reading-w pt-5">{activeTabContent}</div>

          <CustomerVoucherDrawer
            voucher={drawerVoucher}
            onClose={() => {
              setDrawerVoucher(null);
            }}
          />
        </div>
      }
    />
  );
}

function customerTabContent(
  activeTab: CustomerTab,
  dossier: CustomerDossier,
  onOpenVoucher: (voucher: VoucherView) => void,
): React.JSX.Element {
  if (activeTab === 'inactive') {
    return (
      <CustomerInactiveTab
        inactiveVouchers={dossier.trips.inactiveVouchers}
        onOpenVoucher={onOpenVoucher}
      />
    );
  }
  if (activeTab === 'company') return <CustomerCompanyTab company={dossier.company} />;
  return <CustomerProfilePanel profile={dossier.profile} hotelLoyalty={dossier.hotelLoyalty} />;
}
